import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { copilotChat } from "../lib/api";
import { MicButton } from "./MicButton";

const QUICK_ASKS = [
  "Make this trip more budget friendly",
  "Give me more hidden gems",
  "Which spot is best for sunset reels?",
  "Create captions for this itinerary",
  "Make this itinerary more romantic",
  "Avoid overcrowded places",
  "Give me a one-day version",
];

const SPOT_QUICK_ASKS = [
  "Best photo angles here?",
  "Write me 3 caption options",
  "What time of day looks best?",
  "Reel hook idea for this spot",
  "What to wear for this aesthetic",
  "Anything nearby I shouldn't miss?",
];

export const Copilot = ({ itineraryId, itineraryTitle, spotName, openSignal = 0, label = "Ask AI" }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Allow parent pages to programmatically open the Copilot (e.g., from a CTA card)
  useEffect(() => {
    if (openSignal > 0) setOpen(true);
  }, [openSignal]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const quickAsks = spotName ? SPOT_QUICK_ASKS : QUICK_ASKS;

  const send = async (text) => {
    let msg = (text ?? input).trim();
    if (!msg) return;
    // Auto-prefix spot context so the LLM grounds answers to the spot the user is viewing
    const finalMsg = spotName ? `About "${spotName}": ${msg}` : msg;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const r = await copilotChat({ message: finalMsg, itinerary_id: itineraryId, session_id: sessionId });
      if (r.session_id) setSessionId(r.session_id);
      setMessages((m) => [...m, { role: "ai", text: r.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: "Hmm, my signal dropped. Try again in a sec." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="copilot-toggle"
        className="fixed bottom-6 right-24 sm:right-28 z-50 gradient-pink-cyan rounded-full pl-4 pr-5 py-3 text-black glow-pink hover:scale-105 active:scale-95 transition inline-flex items-center gap-2 font-display font-bold uppercase tracking-[0.18em] text-[11px] shadow-[0_8px_30px_rgba(254,9,121,0.45)]"
        aria-label={open ? "Close Copilot" : `Open ${label}`}
      >
        {open ? <X className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
        <span className="hidden sm:inline">{open ? "Close" : label}</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.2, 0.7, 0.2, 1] }}
            className="fixed bottom-24 right-4 z-50 w-[92vw] max-w-sm h-[70vh] sm:h-[560px] glass-strong rounded-3xl border border-white/10 overflow-hidden flex flex-col"
            data-testid="copilot-panel"
          >
            <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
              <span className="h-7 w-7 rounded-full gradient-pink-cyan flex items-center justify-center">
                <Sparkles className="h-3.5 w-3.5 text-black" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm">Ask VYBE</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 truncate">
                  {spotName || itineraryTitle || "Your AI travel friend"}
                </div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.length === 0 && (
                <p className="text-zinc-300 text-sm">
                  {spotName
                    ? `Yo. Ask me anything about ${spotName} — best angles, captions, timing, what to wear, nearby gems.`
                    : "Yo. Ask me to remix this trip — budget cuts, hidden gems, captions, sunset spots, anything."}
                </p>
              )}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "gradient-pink-cyan text-black font-medium" : "bg-white/5 border border-white/10 text-zinc-100"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-sm text-zinc-300 inline-flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                  </div>
                </div>
              )}
            </div>

            {/* Persistent quick-ask rail — visible throughout the conversation */}
            <div className="px-3 pt-2 pb-1 border-t border-white/5">
              <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1">
                {quickAsks.map((q) => (
                  <button
                    key={q}
                    data-testid={`copilot-quick-${q.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => send(q)}
                    disabled={loading}
                    className="shrink-0 text-[11px] font-medium text-zinc-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-2.5 py-1.5 transition disabled:opacity-50"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-3 border-t border-white/5 flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask VYBE…"
                data-testid="copilot-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm outline-none placeholder:text-zinc-500 focus:border-white/25 transition"
                disabled={loading}
              />
              <MicButton
                size="sm"
                testId="copilot-mic-btn"
                onTranscript={(text) => setInput((v) => (v ? `${v.trim()} ${text}` : text))}
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="copilot-send"
                className="gradient-pink-cyan rounded-full p-2.5 text-black hover:scale-105 active:scale-95 transition disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
